import React, { useState } from "react";
import { Plus, Pencil, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BrandLogo } from "@/components/BrandLogo";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { brands, validationRules, deals } from "@/lib/mock-data";
import { Gated } from "@/components/Gated";
import { toast } from "sonner";

// ADM-14 — Brands & validation rules
const SECTIONS = ["brands", "rules"];

export default function BrandsRulesPage() {
  const [section, setSection] = useState("brands");
  const [editRule, setEditRule] = useState(null);
  const [newBrandOpen, setNewBrandOpen] = useState(false);

  const dealsByBrand = brands.reduce((a, b) => {
    a[b.id] = deals.filter((d) => d.brandId === b.id).length;
    return a;
  }, {});

  return (
    <div data-testid="brands-rules-page" className="space-y-5 pb-10">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow">Configuration</div>
          <h1 className="font-brand text-display text-ink leading-tight mt-1">Brands &amp; validation rules</h1>
          <p className="text-body-sm text-ink-muted mt-1">Manage the brands you buy and the rules cards must pass.</p>
        </div>
      </div>

      <div className="flex bg-surface-alt rounded-md p-0.5 w-fit">
        {SECTIONS.map((s) => (
          <button key={s} onClick={() => setSection(s)} data-testid={`config-tab-${s}`}
                  className={`px-4 py-1.5 rounded-md text-caption font-semibold capitalize transition-all ${
                    section === s ? "bg-surface text-ink shadow-xs" : "text-ink-muted hover:text-ink"
                  }`}>{s}</button>
        ))}
      </div>

      {section === "brands" ? (
        <div className="-mx-8 bg-surface border-y border-line">
          <div className="px-8 py-4 border-b border-line flex items-center justify-between">
            <h2 className="text-h3 text-ink font-semibold">Brands <span className="text-ink-hint font-normal tabular-nums">({brands.length})</span></h2>
            <Gated action="brands.create">
              <Button onClick={() => setNewBrandOpen(true)} data-testid="new-brand-btn" className="bg-brand-700 hover:bg-brand-800 text-white">
                <Plus className="size-4 mr-1.5" /> New brand
              </Button>
            </Gated>
          </div>
          <table className="w-full text-body-sm">
            <thead className="bg-surface-alt">
              <tr className="text-left">
                <th className="px-8 py-3 text-eyebrow uppercase text-ink-hint">Brand</th>
                <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint">Default validation rules</th>
                <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint text-right">Active deals</th>
                <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint text-right">All-time deals</th>
                <th className="px-8 py-3 text-eyebrow uppercase text-ink-hint text-right"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line/60">
              {brands.map((b) => (
                <tr key={b.id} className="hover:bg-surface-alt/40">
                  <td className="px-8 py-3.5">
                    <div className="flex items-center gap-3">
                      <BrandLogo brand={b} size="sm" />
                      <div>
                        <div className="text-ink font-semibold">{b.name}</div>
                        <div className="text-caption text-ink-subtle font-mono">{b.id}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3.5 text-ink-muted text-caption">
                    {b.defaultValidationRuleIds?.length
                      ? <div className="flex flex-wrap gap-1.5">
                          {b.defaultValidationRuleIds.slice(0, 3).map((rid) => (
                            <span key={rid} className="inline-flex items-center text-[11px] font-mono px-1.5 py-0.5 rounded bg-surface-alt border border-line text-ink-muted">{rid.replace(/^rule_/, "")}</span>
                          ))}
                          {b.defaultValidationRuleIds.length > 3 && (
                            <span className="text-[11px] text-ink-hint">+{b.defaultValidationRuleIds.length - 3}</span>
                          )}
                        </div>
                      : <span className="text-ink-hint">none</span>}
                  </td>
                  <td className="px-3 py-3.5 text-ink tabular-nums text-right">{deals.filter((d) => d.brandId === b.id && d.status === "Published").length}</td>
                  <td className="px-3 py-3.5 text-ink-muted tabular-nums text-right">{dealsByBrand[b.id]}</td>
                  <td className="px-8 py-3.5 text-right">
                    <button className="text-caption font-semibold text-brand-700 hover:text-brand-800 inline-flex items-center gap-1">
                      <Pencil className="size-3" /> Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="-mx-8 bg-surface border-y border-line">
          <div className="px-8 py-4 border-b border-line flex items-center justify-between">
            <h2 className="text-h3 text-ink font-semibold">Validation rules <span className="text-ink-hint font-normal tabular-nums">({validationRules.length})</span></h2>
            <Button className="bg-brand-700 hover:bg-brand-800 text-white">
              <Plus className="size-4 mr-1.5" /> New rule
            </Button>
          </div>
          <table className="w-full text-body-sm">
            <thead className="bg-surface-alt">
              <tr className="text-left">
                <th className="px-8 py-3 text-eyebrow uppercase text-ink-hint">Rule</th>
                <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint">Description</th>
                <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint">Severity</th>
                <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint text-right">Used in</th>
                <th className="px-8 py-3 text-eyebrow uppercase text-ink-hint text-right"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line/60">
              {validationRules.map((r) => {
                const usedIn = deals.filter((d) => d.validationRuleIds.includes(r.id)).length;
                return (
                  <tr key={r.id} className="hover:bg-surface-alt/40">
                    <td className="px-8 py-3.5">
                      <div className="text-ink font-semibold">{r.name}</div>
                      <div className="text-caption text-ink-subtle font-mono">{r.id}</div>
                    </td>
                    <td className="px-3 py-3.5 text-ink-muted text-caption max-w-[420px]">{r.description}</td>
                    <td className="px-3 py-3.5">
                      <span className={`text-caption font-medium px-2 py-0.5 rounded ${r.severity === "reject" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"}`}>{r.severity}</span>
                    </td>
                    <td className="px-3 py-3.5 text-ink tabular-nums text-right">{usedIn} deals</td>
                    <td className="px-8 py-3.5 text-right">
                      <button onClick={() => setEditRule(r)}
                              className="text-caption font-semibold text-brand-700 hover:text-brand-800 inline-flex items-center gap-1">
                        <Pencil className="size-3" /> Edit
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit rule modal */}
      <Dialog open={!!editRule} onOpenChange={(v) => !v && setEditRule(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit rule: {editRule?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-caption font-semibold text-ink mb-1.5 block">Name</Label>
              <Input defaultValue={editRule?.name} />
            </div>
            <div>
              <Label className="text-caption font-semibold text-ink mb-1.5 block">Description</Label>
              <Textarea rows={3} defaultValue={editRule?.description} />
            </div>
            <div>
              <Label className="text-caption font-semibold text-ink mb-1.5 block">Severity</Label>
              <Select defaultValue={editRule?.severity}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="warn">Warn (flag for admin review)</SelectItem>
                  <SelectItem value="reject">Reject (auto-reject the card)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditRule(null)}>Cancel</Button>
            <Button onClick={() => { setEditRule(null); toast.success("Rule updated"); }} className="bg-brand-700 hover:bg-brand-800 text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={newBrandOpen} onOpenChange={setNewBrandOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New brand</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-caption font-semibold text-ink mb-1.5 block">Brand name</Label>
              <Input placeholder="e.g., Whole Foods Market" />
            </div>
            <div>
              <Label className="text-caption font-semibold text-ink mb-1.5 block">Brand ID slug</Label>
              <Input placeholder="brn_wholefoods" className="font-mono" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewBrandOpen(false)}>Cancel</Button>
            <Button onClick={() => { setNewBrandOpen(false); toast.success("Brand created"); }} className="bg-brand-700 hover:bg-brand-800 text-white">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
